/**
 * Lógica pura del selector de categoría móvil (H76): qué categorías se
 * ofrecen, en qué orden salen las secciones (Listo para asignar, Sugerida,
 * Recientes y los grupos) y la lista de recientes guardada en el móvil.
 */
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

/** Recientes que se enseñan. */
export const NUM_RECIENTES = 3;
/** Se guarda una más por si la sugerida coincide con alguna reciente. */
const RECIENTES_GUARDADAS = NUM_RECIENTES + 1;
const CLAVE_RECIENTES = 'rumbo:categorias-recientes';

export type GrupoVisible = {
  grupo: CategoryGroupEntity;
  categorias: CategoryEntity[];
};

export type BloqueCategoria =
  | { tipo: 'listo'; categoria: CategoryEntity }
  | { tipo: 'sugerida'; categoria: CategoryEntity }
  | { tipo: 'recientes'; categorias: CategoryEntity[] }
  | ({ tipo: 'grupo' } & GrupoVisible);

/**
 * Grupos con sus categorías elegibles. Las ocultas (o de un grupo oculto)
 * nunca se ofrecen: si son ocultas, son ocultas. Los grupos que se quedan
 * sin categorías no salen.
 */
export function gruposVisibles(grupos: CategoryGroupEntity[]): GrupoVisible[] {
  return grupos
    .filter(g => !g.hidden && !g.tombstone)
    .map(grupo => ({
      grupo,
      categorias: (grupo.categories ?? []).filter(
        c => c.group === grupo.id && !c.hidden && !c.tombstone,
      ),
    }))
    .filter(g => g.categorias.length > 0);
}

/**
 * Categoría de ingreso que hace de «Listo para asignar»: la que se llame así
 * (o «Income»/«Ingresos»); si no, la primera visible que no sea la de
 * saldos iniciales.
 */
export function categoriaIngreso(
  categorias: CategoryEntity[],
): CategoryEntity | null {
  const ingresos = categorias.filter(c => c.is_income && !c.hidden);
  return (
    ingresos.find(c =>
      /ready to assign|listo para asignar|^(income|ingresos?)$/i.test(
        c.name.trim(),
      ),
    ) ??
    ingresos.find(c => !/starting balances|saldos? inicial/i.test(c.name)) ??
    ingresos[0] ??
    null
  );
}

/**
 * Orden de la hoja: «Listo para asignar» primero si es un ingreso; luego la
 * sugerida (la última categoría con ese beneficiario); las recientes (sin
 * repetir la sugerida; en un ingreso no salen, manda «Listo para asignar»);
 * y todos los grupos. Solo entra lo que está en `grupos`.
 */
export function bloquesCategoria({
  grupos,
  sugeridaId,
  recientes,
  ingreso,
}: {
  grupos: GrupoVisible[];
  sugeridaId?: string | null;
  recientes: string[];
  ingreso?: CategoryEntity | null;
}): BloqueCategoria[] {
  const porId = new Map<string, CategoryEntity>();
  for (const g of grupos) {
    for (const c of g.categorias) {
      porId.set(c.id, c);
    }
  }
  const bloques: BloqueCategoria[] = [];
  const listo = ingreso ? porId.get(ingreso.id) : undefined;
  if (listo) {
    bloques.push({ tipo: 'listo', categoria: listo });
  }
  const sugerida = sugeridaId ? porId.get(sugeridaId) : undefined;
  if (sugerida && sugerida.id !== listo?.id) {
    bloques.push({ tipo: 'sugerida', categoria: sugerida });
  }
  if (!listo) {
    const ultimas = recientes
      .filter(id => id !== sugerida?.id)
      .map(id => porId.get(id))
      .filter((c): c is CategoryEntity => c != null)
      .slice(0, NUM_RECIENTES);
    if (ultimas.length > 0) {
      bloques.push({ tipo: 'recientes', categorias: ultimas });
    }
  }
  for (const g of grupos) {
    bloques.push({ tipo: 'grupo', ...g });
  }
  return bloques;
}

/** Pone `id` la primera, sin repetir, y recorta la lista. */
export function anadirReciente(lista: string[], id: string): string[] {
  return [id, ...lista.filter(x => x !== id)].slice(0, RECIENTES_GUARDADAS);
}

/** Recientes guardadas en este móvil (vacía si el almacenamiento falla). */
export function leerRecientes(): string[] {
  try {
    const crudo = window.localStorage.getItem(CLAVE_RECIENTES);
    const lista: unknown = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(lista)
      ? lista.filter((x): x is string => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
}

/** Apunta una categoría elegida como reciente. */
export function guardarReciente(id: string): void {
  try {
    window.localStorage.setItem(
      CLAVE_RECIENTES,
      JSON.stringify(anadirReciente(leerRecientes(), id)),
    );
  } catch {
    // Sin almacenamiento (modo privado): las recientes no se recuerdan.
  }
}
